import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useToast, SimpleGrid } from "@chakra-ui/react";
import { getPlatform, getProducts, makePurchase, Product } from "@/lib/iap";
import { callWithAuth, getUserInfo, getUserStats } from "@/api/Api";
import { LoadingPage } from "./LoadingPage";
import { TimeStatBlock } from "@/components/StatBlock";
import { StatBlock } from "@/components/StatBlock";
import PremiumBlock from "@/components/Premium/PremiumBlock";
import PremiumFooter from "@/components/Premium/PremiumFooter";
import NoPaymentText from "@/components/NoPaymentText";
import { usePostHog } from "posthog-js/react";

function useProducts() {
  const [amount, setAmount] = useState("");
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  useEffect(() => {
    if (productsLoaded) {
      return;
    }
    setProductsLoaded(true);
    getProducts().then((products) => {
      setProducts(products);
      setAmount(products[1].price);
    });
  }, [productsLoaded]);
  return { products, productsLoaded, amount, setAmount };
}

const PLATFORM = getPlatform();

const Upgrade: React.FC = () => {
  const { "*": backUrl } = useParams();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState("Monthly");
  const [selectedSku, setSelectedSku] = useState("monthly_sub");
  const { products, productsLoaded, amount, setAmount } = useProducts();
  const toast = useToast();
  const [isSubscribing, setIsSubscribing] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const posthog = usePostHog();

  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });
  const runOnce = useRef(false);

  useEffect(() => {
    if (runOnce.current) return;
    runOnce.current = true;
    posthog.capture("upgrade_page_viewed");
  }, []);

  const handlePaymentClose = () => {
    setIsSubscribing(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  useEffect(() => {
    window.addEventListener("payment-window-closed", handlePaymentClose);
    return () => {
      window.removeEventListener("payment-window-closed", handlePaymentClose);
    };
  }, []);

  const handleSubscribe = async () => {
    if (isSubscribing) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsSubscribing(false);
      return;
    }
    setIsSubscribing(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      if (selectedSku && PLATFORM === "apple") {
        const isSuccess = await makePurchase(
          selectedSku,
          abortController.signal
        );
        if (isSuccess === null) {
          return;
        }
        if (!isSuccess) {
          toast({
            title: "Subscription Error",
            description:
              "Something went wrong, please try again or contact me at 778-228-6108 and I will personally fix your problem. - Erik Dahl Parrot Founder",
            status: "error",
            duration: 10000,
            isClosable: true,
            position: "top",
          });
        } else {
          posthog.capture("upgrade_page_success_ios");
          if (userInfoQuery.data?.firstName == "") {
            navigate("/name");
          } else {
            navigate("/home/learn");
          }
          return;
        }
      } else if (selectedSku && PLATFORM === "web") {
        navigate(`/checkout/${backUrl}?sku=${selectedSku}`);
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Subscription process was canceled");
        return;
      } else {
        console.error("Subscription error:", error);
      }
    } finally {
      setIsSubscribing(false);
      abortControllerRef.current = null;
    }
  };

  if (!productsLoaded) {
    return <LoadingPage />;
  }

  if (userInfoQuery.isPending) {
    return <LoadingPage />;
  }

  const displayName = userInfoQuery.data?.firstName;

  const isFreeTrialUsed = userInfoQuery.data?.isFreeTrialUsed || false;
  const useMonthlyFreeTrialUI = selectedPlan === "Monthly" && !isFreeTrialUsed;

  return (
    <div className="appPadding flex flex-col items-center justify-between w-full h-full overflow-hidden">
      <div className="flex flex-col flex-grow justify-start gap-4 overflow-y-auto w-full h-full z-40 pt-8">
        <div className="text-center text-3xl font-extrabold">
          Thanks for learning with Parrot{displayName ? `, ${displayName}` : ""}
          ! Upgrade to save your progress.
        </div>
        <div className="flex items-center justify-center gap-2 text-center mt-2">
          We've been working hard to make this the best language learning
          experience for you. Ready for more? Upgrade now to unlock all the
          features.
        </div>
        <div className="flex flex-grow w-full justify-center items-center">
          <FreeUserProgress />
        </div>
        <div className="flex flex-row gap-4 pb-5">
          {products.map((product) => (
            <PremiumBlock
              key={product.sku}
              currency={product.currency}
              heading={product.name}
              price={
                product.name === "Monthly"
                  ? (
                      Math.floor((Number(product.price) / 4) * 100) / 100
                    ).toFixed(2)
                  : product.price
              }
              // This one always needs to be there, not dependong on selected plan
              isFreeTrial={product.name === "Monthly" && !isFreeTrialUsed}
              freeTrialDays={3}
              onClick={() => {
                setAmount(product.price);
                setSelectedPlan(product.name);
                setSelectedSku(product.sku);
              }}
              selected={selectedPlan === product.name}
            />
          ))}
        </div>
      </div>
      <div className="w-full">
        {selectedPlan === "Monthly" && !isFreeTrialUsed ? (
          <NoPaymentText />
        ) : (
          <div className="h-6"></div>
        )}
        <PremiumFooter
          platform={PLATFORM}
          amount={amount}
          period={selectedPlan === "Weekly" ? "weekly" : "monthly"}
          showFreeTrialText={useMonthlyFreeTrialUI}
          onContinue={handleSubscribe}
          currency={
            products.find((product) => product.sku === selectedSku)?.currency ||
            "USD"
          }
        />
      </div>
    </div>
  );
};

export default Upgrade;

function FreeUserProgress() {
  const userStatsQuery = useQuery({
    queryKey: ["userStats"],
    queryFn: () => {
      return callWithAuth(getUserStats);
    },
  });

  const listeningTime = userStatsQuery.data?.listeningTime || 0;
  const wordsKnown = userStatsQuery.data?.wordsKnown || 0;
  const waterDropletCount = userStatsQuery.data?.waterDropletsCount || 0;
  const treesPlanted = userStatsQuery.data?.treesPlanted || 0;

  return (
    <div className="flex flex-col gap-4 w-full">
      <SimpleGrid columns={2} spacing={3}>
        <TimeStatBlock timeSeconds={listeningTime} postText=" ⏰" />
        <StatBlock
          value={wordsKnown}
          postText=" 🇪🇸"
          subtitle="Words Added"
          aspectRatio={2}
        />
        <StatBlock
          value={waterDropletCount}
          postText=" 💧"
          subtitle="Water Drops"
          aspectRatio={2}
        />
        <StatBlock
          value={treesPlanted}
          postText=" 🌳"
          subtitle="Trees Planted"
          aspectRatio={2}
        />
      </SimpleGrid>
    </div>
  );
}
